import { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from '@mui/material';
import {
  Description as TextIcon,
  Image as ImageIcon,
  Person as PersonIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '@/services/adminApi';

/**
 * Admin Dashboard Page
 * 관리자 대시보드 페이지
 */
const DashboardPage = () => {
  const [chartDays, setChartDays] = useState<number>(7);

  // 전체 통계
  const { data: statsData, isLoading: isStatsLoading, isError: isStatsError, error: statsError } = useQuery({
    queryKey: ['admin', 'dashboard', 'stats'],
    queryFn: () => adminApi.getDashboardStats(),
    refetchInterval: 60000, // 1분마다 자동 갱신
  });

  // 기간별 로그 통계
  const { data: logsByDateData, isLoading: isLogsByDateLoading } = useQuery({
    queryKey: ['admin', 'dashboard', 'logs-by-date', chartDays],
    queryFn: () => adminApi.getLogStatsByDateRange(chartDays),
  });

  // 상위 사용자
  const { data: topUsersData, isLoading: isTopUsersLoading } = useQuery({
    queryKey: ['admin', 'dashboard', 'top-users'],
    queryFn: () => adminApi.getTopUsers(5),
  });

  // 상위 실험 키
  const { data: topExperimentKeysData, isLoading: isTopExperimentKeysLoading } = useQuery({
    queryKey: ['admin', 'dashboard', 'top-experiment-keys'],
    queryFn: () => adminApi.getTopExperimentKeys(5),
  });

  // 토큰 사용량 통계
  const { data: tokenUsageData, isLoading: isTokenUsageLoading } = useQuery({
    queryKey: ['admin', 'dashboard', 'token-usage'],
    queryFn: () => adminApi.getTokenUsageStats(),
  });

  // 최근 활동
  const { data: recentActivityData, isLoading: isRecentActivityLoading } = useQuery({
    queryKey: ['admin', 'dashboard', 'recent-activity'],
    queryFn: () => adminApi.getRecentActivity(10),
  });

  const handleChartDaysChange = (_event: React.MouseEvent<HTMLElement>, newValue: number | null) => {
    if (newValue !== null) {
      setChartDays(newValue);
    }
  };

  // 백엔드는 ApiResponseDto로 응답하므로 data.data가 실제 데이터 객체
  const stats = statsData?.data || {};
  const logsByDate = Array.isArray(logsByDateData?.data) ? logsByDateData.data : [];
  const topUsers = Array.isArray(topUsersData?.data) ? topUsersData.data : [];
  const topExperimentKeys = Array.isArray(topExperimentKeysData?.data) ? topExperimentKeysData.data : [];
  const tokenUsage = tokenUsageData?.data || {};
  const recentActivity = Array.isArray(recentActivityData?.data) ? recentActivityData.data : [];

  // 차트 데이터 포맷팅 (날짜 형식 변경)
  const chartData = logsByDate.map((item: any) => ({
    date: item.date ? new Date(item.date).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' }) : item.date,
    '텍스트 생성': item.textLogs || 0,
    '이미지 생성': item.imageLogs || 0,
  }));

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        대시보드
      </Typography>

      {/* 통계 카드 그리드 */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TextIcon color="primary" />
                <Typography color="text.secondary" variant="body2">
                  전체 텍스트 생성 로그
                </Typography>
              </Box>
              <Typography variant="h4">
                {isStatsLoading ? <CircularProgress size={24} /> : (stats.totalTextLogs || 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ImageIcon color="primary" />
                <Typography color="text.secondary" variant="body2">
                  전체 이미지 생성 로그
                </Typography>
              </Box>
              <Typography variant="h4">
                {isStatsLoading ? <CircularProgress size={24} /> : (stats.totalImageLogs || 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PersonIcon color="primary" />
                <Typography color="text.secondary" variant="body2">
                  활성 사용자 수
                </Typography>
              </Box>
              <Typography variant="h4">
                {isStatsLoading ? <CircularProgress size={24} /> : (stats.activeUsers || 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUpIcon color="primary" />
                <Typography color="text.secondary" variant="body2">
                  오늘 텍스트 생성
                </Typography>
              </Box>
              <Typography variant="h4">
                {isStatsLoading ? <CircularProgress size={24} /> : (stats.todayTextLogs || 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUpIcon color="primary" />
                <Typography color="text.secondary" variant="body2">
                  오늘 이미지 생성
                </Typography>
              </Box>
              <Typography variant="h4">
                {isStatsLoading ? <CircularProgress size={24} /> : (stats.todayImageLogs || 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AssessmentIcon color="primary" />
                <Typography color="text.secondary" variant="body2">
                  총 토큰 사용량
                </Typography>
              </Box>
              <Typography variant="h4">
                {isStatsLoading ? <CircularProgress size={24} /> : (stats.totalTokens || 0).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                입력: {(stats.totalInputTokens || 0).toLocaleString()} / 출력: {(stats.totalOutputTokens || 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 기간별 트렌드 차트 */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">기간별 로그 추이</Typography>
          <ToggleButtonGroup
            value={chartDays}
            exclusive
            onChange={handleChartDaysChange}
            size="small"
          >
            <ToggleButton value={7}>7일</ToggleButton>
            <ToggleButton value={30}>30일</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {isLogsByDateLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : chartData.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', p: 4 }}>
            차트 데이터가 없습니다.
          </Typography>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="텍스트 생성" stroke="#1976d2" strokeWidth={2} />
              <Line type="monotone" dataKey="이미지 생성" stroke="#2e7d32" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Paper>

      {/* 상위 사용자 및 실험 키 */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              상위 사용자
            </Typography>
            {isTopUsersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : topUsers.length === 0 ? (
              <Typography color="text.secondary" sx={{ p: 2 }}>
                데이터가 없습니다.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>사용자 ID</TableCell>
                      <TableCell align="right">텍스트</TableCell>
                      <TableCell align="right">이미지</TableCell>
                      <TableCell align="right">전체</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topUsers.map((user: any, index: number) => (
                      <TableRow key={user.userId || index}>
                        <TableCell>{user.userId || '-'}</TableCell>
                        <TableCell align="right">{(user.textLogCount || 0).toLocaleString()}</TableCell>
                        <TableCell align="right">{(user.imageLogCount || 0).toLocaleString()}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={(user.totalLogCount || 0).toLocaleString()}
                            color="primary"
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              상위 실험 키
            </Typography>
            {isTopExperimentKeysLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : topExperimentKeys.length === 0 ? (
              <Typography color="text.secondary" sx={{ p: 2 }}>
                데이터가 없습니다.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>실험 키</TableCell>
                      <TableCell align="right">텍스트</TableCell>
                      <TableCell align="right">이미지</TableCell>
                      <TableCell align="right">전체</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topExperimentKeys.map((key: any, index: number) => (
                      <TableRow key={key.experimentKey || index}>
                        <TableCell>{key.experimentKey || '-'}</TableCell>
                        <TableCell align="right">{(key.textLogCount || 0).toLocaleString()}</TableCell>
                        <TableCell align="right">{(key.imageLogCount || 0).toLocaleString()}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={(key.totalLogCount || 0).toLocaleString()}
                            color="secondary"
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* 토큰 사용량 통계 */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          토큰 사용량 통계
        </Typography>
        {isTokenUsageLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" variant="body2">
                    총 입력 토큰
                  </Typography>
                  <Typography variant="h5">
                    {(tokenUsage.totalInputTokens || 0).toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" variant="body2">
                    총 출력 토큰
                  </Typography>
                  <Typography variant="h5">
                    {(tokenUsage.totalOutputTokens || 0).toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" variant="body2">
                    평균 토큰
                  </Typography>
                  <Typography variant="h5">
                    {tokenUsage.avgTokens ? Math.round(tokenUsage.avgTokens).toLocaleString() : 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" variant="body2">
                    최대 토큰
                  </Typography>
                  <Typography variant="h5">
                    {(tokenUsage.maxTokens || 0).toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* 최근 활동 */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          최근 활동
        </Typography>
        {isRecentActivityLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : recentActivity.length === 0 ? (
          <Typography color="text.secondary" sx={{ p: 2 }}>
            활동이 없습니다.
          </Typography>
        ) : (
          <Box>
            {recentActivity.map((activity: any) => (
              <Box
                key={activity.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  mb: 1,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {activity.type === 'text' ? (
                  <TextIcon color="primary" />
                ) : (
                  <ImageIcon color="secondary" />
                )}
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Chip
                      label={activity.type === 'text' ? '텍스트 생성' : '이미지 생성'}
                      size="small"
                      color={activity.type === 'text' ? 'primary' : 'secondary'}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {activity.userId}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ({activity.experimentKey})
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {activity.preview || '-'}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {activity.createdAt ? new Date(activity.createdAt).toLocaleString('ko-KR') : '-'}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {isStatsError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          통계를 불러오는데 실패했습니다.{' '}
          {statsError instanceof Error ? statsError.message : '알 수 없는 오류가 발생했습니다.'}
        </Alert>
      )}
    </Box>
  );
};

export default DashboardPage;
